import { Box, Stack, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import { transition } from '@/theme';
import { useTicker } from '../motion';

const FILES = [
  { name: 'almoco-26-09.jpg', icon: ImageRounded, color: '#e37400' },
  { name: 'extrato-setembro.pdf', icon: PictureAsPdfRounded, color: '#d93025' },
  { name: 'jantar-25-09.jpg', icon: ImageRounded, color: '#e37400' },
];

/** Uploaded photos and documents land in the student's own Google Drive (illustrative). */
export function DriveDemo({ folder }: { folder: string }) {
  const step = useTicker(FILES.length + 2, 1400);
  const shown = Math.min(step, FILES.length);
  const sending = step < FILES.length;

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' } }}
      >
        <Box
          aria-hidden
          sx={(theme) => ({
            display: { xs: 'none', sm: 'grid' },
            width: 64,
            height: 64,
            flexShrink: 0,
            borderRadius: 3,
            placeItems: 'center',
            bgcolor: theme.vars.palette.secondary.container,
            color: theme.vars.palette.secondary.onContainer,
          })}
        >
          <CameraAltRounded />
        </Box>
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: 96,
            flexShrink: 0,
            position: 'relative',
            height: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            key={step}
            sx={(theme) => ({
              position: 'absolute',
              top: 6,
              width: 12,
              height: 12,
              borderRadius: 1,
              bgcolor: theme.vars.palette.primary.main,
              opacity: sending ? 1 : 0,
              animation: sending ? 'drive-fly 1300ms cubic-bezier(0.2, 0, 0, 1) forwards' : 'none',
              '@keyframes drive-fly': {
                from: { left: 0, transform: 'scale(0.6)' },
                to: { left: 'calc(100% - 12px)', transform: 'scale(1)' },
              },
            })}
          />
          <ArrowForwardRounded
            sx={{ position: 'absolute', right: 0, top: 0, color: 'text.disabled', fontSize: 24 }}
          />
        </Box>
        <Box
          sx={(theme) => ({
            width: { xs: '100%', sm: 'auto' },
            flex: { sm: 1 },
            minWidth: 0,
            p: 1.5,
            borderRadius: 3,
            bgcolor: '#fff',
            color: '#202124',
            border: `1px solid ${theme.vars.palette.divider}`,
          })}
        >
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 1 }}>
            <FolderRounded sx={{ fontSize: 18, color: '#5f6368' }} />
            <Typography sx={{ fontSize: 12, color: '#5f6368' }} noWrap>
              Meu Drive › {folder}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            {FILES.map((file, i) => (
              <Stack
                key={file.name}
                direction="row"
                spacing={0.75}
                sx={{
                  alignItems: 'center',
                  opacity: i < shown ? 1 : 0.12,
                  transform: i < shown ? 'none' : 'translateX(-6px)',
                  transition: transition(['opacity', 'transform'], 'medium4'),
                }}
              >
                <file.icon sx={{ fontSize: 16, color: file.color }} />
                <Typography sx={{ fontSize: 12.5 }} noWrap>
                  {file.name}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
        <LockRounded sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          O app só enxerga os arquivos que ele mesmo criou. O resto do Drive continua privado.
        </Typography>
      </Stack>
    </Stack>
  );
}
